import { useRef, useState, type PointerEvent } from 'react';
import { Button } from './Button';
import { cx } from './cx';

export interface SignaturePadProps {
  /** The signature as a PNG data URL, or null when nothing is drawn. */
  value: string | null;
  onChange: (png: string | null) => void;
  label?: string | undefined;
  width?: number | undefined;
  height?: number | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

/**
 * Sign with a finger, pen or mouse. Gives back a PNG data URL. In Phase 2 it is kept in memory
 * only (printed on the airway bill); uploading it with the shipment comes later.
 */
export function SignaturePad({
  value,
  onChange,
  label = 'Signature',
  width = 520,
  height = 180,
  disabled,
  className,
}: SignaturePadProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [inked, setInked] = useState(false);

  const point = (e: PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const el = e.currentTarget;
    const box = el.getBoundingClientRect();
    const sx = box.width > 0 ? width / box.width : 1;
    const sy = box.height > 0 ? height / box.height : 1;
    return { x: ((e.clientX || 0) - box.left) * sx, y: ((e.clientY || 0) - box.top) * sy };
  };

  const context = (): CanvasRenderingContext2D | null => {
    try {
      return canvas.current?.getContext('2d') ?? null;
    } catch {
      return null;
    }
  };

  const down = (e: PointerEvent<HTMLCanvasElement>): void => {
    if (disabled) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const ctx = context();
    if (!ctx) return;
    const p = point(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0b1426';
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.01, p.y + 0.01);
    ctx.stroke();
  };

  const move = (e: PointerEvent<HTMLCanvasElement>): void => {
    if (!drawing.current) return;
    const ctx = context();
    if (!ctx) return;
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const up = (): void => {
    if (!drawing.current) return;
    drawing.current = false;
    setInked(true);
    onChange(canvas.current?.toDataURL('image/png') ?? null);
  };

  const clear = (): void => {
    const el = canvas.current;
    context()?.clearRect(0, 0, el?.width ?? width, el?.height ?? height);
    setInked(false);
    onChange(null);
  };

  return (
    <div className={cx('eb-sign', className)}>
      <canvas
        ref={canvas}
        className="eb-sign__canvas"
        width={width}
        height={height}
        role="img"
        aria-label={`${label} pad. ${value || inked ? 'Signed.' : 'Not signed yet.'}`}
        data-signed={value !== null || inked ? 'true' : 'false'}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
      />
      <div className="eb-sign__bar">
        <span className="eb-muted">
          {value || inked ? 'Signed' : 'Sign above with a finger, pen or mouse'}
        </span>
        <Button variant="ghost" size="sm" onClick={clear} disabled={disabled || !(value || inked)}>
          Clear signature
        </Button>
      </div>
    </div>
  );
}
